import { useState, type FormEvent } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  useDataProvider,
  useGetIdentity,
  useNotify,
  useTranslate,
} from "ra-core";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import {
  QUOTE_PLAN_TYPES,
  type Project,
  type QuotePlanTemplate,
} from "../types";
import { PlanSchedule, tryCalculateSchedule } from "./PlanSchedule";
import {
  EMPTY_PLAN_TEMPLATE_FORM,
  planTemplateToFormValues,
  toPlanParameters,
  validatePlanTemplateForm,
  type PlanNumberField,
  type PlanTemplateFormValues,
} from "./planTemplateRules";

export const PLAN_TEMPLATES_QUERY_KEY = "quote_plan_templates_admin";

/** Sample price used by the preview schedule (MXN). */
const SAMPLE_AMOUNT = 1_000_000;

const FIELD_CLASS = "h-11 sm:h-9";
const SELECT_CLASS =
  "h-11 w-full rounded-md border bg-background px-3 text-sm sm:h-9";

/** Percentage / count pairs, in the order a plan is read. */
const NUMBER_ROWS: { pct: PlanNumberField; count?: PlanNumberField }[] = [
  { pct: "down_payment_pct", count: "down_payment_count" },
  { pct: "monthly_payments_pct", count: "monthly_payments_count" },
  { pct: "deferred_payments_pct", count: "deferred_payments_count" },
  { pct: "delivery_pct" },
  { pct: "credit_pct" },
];

/**
 * Almikia Epic EQ (TASK-018): create / edit dialog of a payment plan template
 * (direccion only; the list mounts it). The live sum of the percentages blocks
 * saving until it is exactly 100, and a preview schedule for a sample amount is
 * computed by the shared calculator. The project cannot change on an existing
 * template. There is no delete: templates are archived from the list.
 */
export const PlanTemplateForm = ({
  template,
  projects,
  onClose,
}: {
  template: QuotePlanTemplate | null;
  projects: Project[];
  onClose: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const queryClient = useQueryClient();
  const dataProvider = useDataProvider();
  const { data: identity } = useGetIdentity();

  const [values, setValues] = useState<PlanTemplateFormValues>(
    template ? planTemplateToFormValues(template) : EMPTY_PLAN_TEMPLATE_FORM,
  );
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);

  const errors = validatePlanTemplateForm(values);
  const hasErrors = Object.keys(errors).length > 0;
  const set = <K extends keyof PlanTemplateFormValues>(
    key: K,
    value: PlanTemplateFormValues[K],
  ) => setValues((current) => ({ ...current, [key]: value }));
  // The sum error is always visible (live); the other ones show after a first submit.
  const err = (field: keyof typeof errors) =>
    errors[field] && (submitted || field === "sum")
      ? translate(`crm.quoter.templateForm.errors.${errors[field]}`)
      : null;

  const schedule = hasErrors
    ? null
    : tryCalculateSchedule(SAMPLE_AMOUNT, toPlanParameters(values));

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (hasErrors || values.plan_type === "") return;
    setSaving(true);
    const data = {
      name: values.name.trim(),
      plan_type: values.plan_type,
      ...toPlanParameters(values),
    };
    try {
      if (template) {
        await dataProvider.update<QuotePlanTemplate>("quote_plan_templates", {
          id: template.id,
          data,
          previousData: template,
        });
      } else {
        // RLS requires created_by = current sales id on insert; nothing server-side defaults it.
        await dataProvider.create<QuotePlanTemplate>("quote_plan_templates", {
          data: {
            ...data,
            project_id: Number(values.project_id),
            created_by: identity?.id,
          },
        });
      }
    } catch {
      setSaving(false);
      notify("crm.quoter.templateForm.save_error", { type: "error" });
      return;
    }
    await queryClient.invalidateQueries({
      queryKey: [PLAN_TEMPLATES_QUERY_KEY],
    });
    notify("crm.quoter.templateForm.saved", { type: "success" });
    onClose();
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-9/10 overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {translate(
              template
                ? "crm.quoter.templateForm.edit_title"
                : "crm.quoter.templateForm.create_title",
            )}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={onSubmit} noValidate className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="plan-form-project">
              {translate("crm.quoter.templateForm.project")}
            </Label>
            <select
              id="plan-form-project"
              className={SELECT_CLASS}
              value={values.project_id}
              disabled={template != null}
              aria-invalid={err("project_id") ? true : undefined}
              onChange={(e) => set("project_id", e.target.value)}
            >
              <option value="">
                {translate("crm.quoter.templateForm.project_placeholder")}
              </option>
              {projects
                .filter(
                  (project) =>
                    !project.archived || project.id === template?.project_id,
                )
                .map((project) => (
                  <option key={project.id} value={String(project.id)}>
                    {project.name}
                  </option>
                ))}
            </select>
            {err("project_id") ? (
              <p role="alert" className="text-sm text-destructive">
                {err("project_id")}
              </p>
            ) : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor="plan-form-name">
              {translate("crm.quoter.templateForm.name")}
            </Label>
            <Input
              id="plan-form-name"
              className={FIELD_CLASS}
              value={values.name}
              aria-invalid={err("name") ? true : undefined}
              onChange={(e) => set("name", e.target.value)}
            />
            {err("name") ? (
              <p role="alert" className="text-sm text-destructive">
                {err("name")}
              </p>
            ) : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor="plan-form-type">
              {translate("crm.quoter.templateForm.plan_type")}
            </Label>
            <select
              id="plan-form-type"
              className={SELECT_CLASS}
              value={values.plan_type}
              aria-invalid={err("plan_type") ? true : undefined}
              onChange={(e) =>
                set(
                  "plan_type",
                  e.target.value as PlanTemplateFormValues["plan_type"],
                )
              }
            >
              <option value="">
                {translate("crm.quoter.templateForm.plan_type_placeholder")}
              </option>
              {QUOTE_PLAN_TYPES.map((type) => (
                <option key={type} value={type}>
                  {translate(`crm.quoter.planTypes.${type}`)}
                </option>
              ))}
            </select>
            {err("plan_type") ? (
              <p role="alert" className="text-sm text-destructive">
                {err("plan_type")}
              </p>
            ) : null}
          </div>

          <fieldset className="space-y-3">
            <legend className="text-sm font-medium">
              {translate("crm.quoter.templateForm.percentages")}
            </legend>
            {NUMBER_ROWS.map(({ pct, count }) => (
              <div key={pct} className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label htmlFor={`plan-form-${pct}`}>
                    {translate(`crm.quoter.templateForm.fields.${pct}`)}
                  </Label>
                  <Input
                    id={`plan-form-${pct}`}
                    inputMode="decimal"
                    className={`${FIELD_CLASS} tabular-nums`}
                    value={values[pct]}
                    aria-invalid={err(pct) ? true : undefined}
                    onChange={(e) => set(pct, e.target.value)}
                  />
                  {err(pct) ? (
                    <p role="alert" className="text-sm text-destructive">
                      {err(pct)}
                    </p>
                  ) : null}
                </div>
                {count ? (
                  <div className="space-y-1">
                    <Label htmlFor={`plan-form-${count}`}>
                      {translate(`crm.quoter.templateForm.fields.${count}`)}
                    </Label>
                    <Input
                      id={`plan-form-${count}`}
                      inputMode="numeric"
                      className={`${FIELD_CLASS} tabular-nums`}
                      value={values[count]}
                      aria-invalid={err(count) ? true : undefined}
                      onChange={(e) => set(count, e.target.value)}
                    />
                    {err(count) ? (
                      <p role="alert" className="text-sm text-destructive">
                        {err(count)}
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ))}
            <div className="space-y-1">
              <Label htmlFor="plan-form-discount_pct">
                {translate("crm.quoter.templateForm.fields.discount_pct")}
              </Label>
              <Input
                id="plan-form-discount_pct"
                inputMode="decimal"
                className={`${FIELD_CLASS} tabular-nums`}
                value={values.discount_pct}
                aria-invalid={err("discount_pct") ? true : undefined}
                onChange={(e) => set("discount_pct", e.target.value)}
              />
              {err("discount_pct") ? (
                <p role="alert" className="text-sm text-destructive">
                  {err("discount_pct")}
                </p>
              ) : null}
            </div>
            {err("sum") ? (
              <p role="alert" className="text-sm font-medium text-destructive">
                {err("sum")}
              </p>
            ) : null}
          </fieldset>

          <section className="space-y-2">
            <h3 className="text-sm font-medium">
              {translate("crm.quoter.templateForm.preview", {
                amount: SAMPLE_AMOUNT.toLocaleString("es-MX"),
              })}
            </h3>
            {schedule ? (
              <PlanSchedule rows={schedule} />
            ) : (
              <p className="text-sm text-muted-foreground">
                {translate("crm.quoter.templateForm.preview_empty")}
              </p>
            )}
          </section>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              className="h-11 sm:h-9"
              onClick={onClose}
            >
              {translate("crm.quoter.templateForm.cancel")}
            </Button>
            <Button
              type="submit"
              className="h-11 sm:h-9"
              disabled={saving || identity == null || errors.sum != null}
            >
              {translate("crm.quoter.templateForm.save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
